import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { IDynamicMarketBetSlip } from '@/interface/IDynamicMarketBetSlip';
import type { IMarket, IMatch, ISelection } from '@/interface/IMatch';
import { BetPageType, BetSlipState, BetSlipType, LeagueSize, PlaceOrderStatus, PriceStyle, StatementDetailStatus } from '@/interface/enum';
import type { DynamicMarketOrderRequest } from '@/interface/pollux';
import type { IVoucherDisplay } from '@/interface/voucher';
import Api, { fireAndForget } from '@/libraries/api';
import { getDynamicMarketOpenTicketErrorMessage, getPlaceBetErrorMessage } from '@/libraries/betSlip/betSlipErrorMessage';
import { saveLastStake } from '@/libraries/betSlip/lastStakeStorage';
import { calculateVoucherMaxPayout, getMaxPayout } from '@/libraries/betSlip/payout';
import { calculateStake, isStakeAtLeastMinBet, isStakeLessBalance, isStakeWithinMaxBet } from '@/libraries/betSlip/stake';
import useState from '@/libraries/composables/useState';
import { useVoucher } from '@/libraries/composables/useVoucher';
import { getDefaultStake, isOddsClose, isPlaceHierarchyClosed } from '@/libraries/helpers/betSlipHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getSportsBsiUrl } from '@/libraries/url';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

export const useDynamicMarketBetSlipStore = defineStore('DynamicMarketBetSlip', () => {
    const [betSlip, setBetSlip] = useState<IDynamicMarketBetSlip>(getDefaultDynamicMarketBetSlip());
    const [stake, setStake] = useState(0);
    const [isPlacingBet, setIsPlacingBet] = useState(false);
    const [betSlipMessage, setBetSlipMessage] = useState<string | null>(null);

    const minBet = computed(() => betSlip.value.minBet);
    const maxBet = computed(() => betSlip.value.maxBet);
    const isLive = computed(() => betSlip.value.match.isLive);
    const price = computed(() => betSlip.value.selection.price?.value ?? 0);

    const [isRedeemVoucher, setIsRedeemVoucher] = useState(false);
    const [voucherList, setVoucherList] = useState<IVoucherDisplay[]>([]);
    const selectedVoucher = ref<IVoucherDisplay | null>(null);

    const { formatMoneyWithSymbolAndDecimal, setBalance } = useAccountStore();
    const { setBetSlipType, setBetSlipState } = useBetSlipStore();

    const stakeOrVoucherStake = computed(() => (isRedeemVoucher.value
        ? selectedVoucher.value?.stake ?? 0
        : stake.value));

    const maxPayout = computed(() => {
        const payout = isRedeemVoucher.value
            ? calculateVoucherMaxPayout(
                stakeOrVoucherStake.value,
                price.value,
                betSlip.value.selection.price?.priceStyle ?? PriceStyle.Unknown,
            )
            : getMaxPayout(stakeOrVoucherStake.value, price.value, betSlip.value.selection.price?.priceStyle ?? PriceStyle.Unknown);

        return formatMoneyWithSymbolAndDecimal(payout);
    });

    const hasTicket = computed(() => betSlip.value.selection.id !== 0);

    const { setTopScreenToast, setShowAcceptAnyOddsBanner, setFailedPlaceBetShowAaoBanner } = useRootStore();

    async function addSelection(matchInfo: IMatch, market: IMarket, selection: ISelection, betPage: BetPageType) {
        const defaultBetSlip = getDefaultDynamicMarketBetSlip();
        setBetSlip({
            ...defaultBetSlip,
            betPage,
            match: {
                leagueId: matchInfo.leagueId,
                leagueSize: matchInfo.leagueSize,
                leagueName: matchInfo.leagueName ?? [],
                matchId: matchInfo.eventId,
                isLive: matchInfo.isLive,
                homeTeamName: matchInfo.homeTeam?.teamName ?? [],
                awayTeamName: matchInfo.awayTeam?.teamName ?? [],
                sportType: matchInfo.sportType,
                liveHomeScore: matchInfo.mainMarketEventResult.liveHomeScore,
                liveAwayScore: matchInfo.mainMarketEventResult.liveAwayScore,
            },
            market: {
                id: market.id,
                name: market.name,
            },
            selection: {
                ...defaultBetSlip.selection,
                id: selection.id,
                name: selection.name,
            },
        });
        setIsRedeemVoucher(false);
        setBetSlipType(BetSlipType.Dynamic);
        setBetSlipState(BetSlipState.Single);
    }

    let cancelToken: CancelTokenSource | undefined;

    const voucherInput = computed(() => ({
        sportType: betSlip.value.match.sportType,
        leagueId: betSlip.value.match.leagueId,
        minBet: betSlip.value.minBet,
        maxBet: betSlip.value.maxBet,
        marketType: betSlip.value.market.id,
        price: price.value,
        priceStyle: betSlip.value.selection.price?.priceStyle ?? null,
    }));

    const { vouchers, fetchVouchers } = useVoucher(voucherInput);

    async function openTicket(isFirstTime: boolean) {
        try {
            if (cancelToken) cancelToken.cancel('invalid previous openTicket');
            cancelToken = axios.CancelToken.source();

            const [ticketResponse] = await Promise.all([
                Api.openDynamicMarketTicket(betSlip.value.selection.id, { cancelToken: cancelToken.token }),
                fetchVouchers(),
            ]);

            if (!ticketResponse.selection) {
                return;
            }

            const isTicketRemove = isOddsClose(ticketResponse.error.code);
            if (isTicketRemove.shouldRemove) {
                setTopScreenToast({
                    status: 'error',
                    messageKey: isTicketRemove.message,
                });
                setTimeout(async () => {
                    await removeTicket();
                }, 3000);
                return;
            }

            setBetSlipMessage(getDynamicMarketOpenTicketErrorMessage(
                ticketResponse.error.code,
                betSlip.value.selection.price,
                ticketResponse.selection.price,
            ));

            setBetSlip({
                ...betSlip.value,
                uid: ticketResponse.uid,
                minBet: ticketResponse.minBet,
                maxBet: ticketResponse.maxBet,
                selection: {
                    ...betSlip.value.selection,
                    price: ticketResponse.selection.price,
                    score: ticketResponse.selection.score,
                    betCondition: ticketResponse.selection.betCondition,
                },
            });

            setBalance(ticketResponse.balance);
            setVoucherList(vouchers.value);

            if (isFirstTime) {
                setStake(getDefaultStake(
                    betSlip.value.minBet,
                    betSlip.value.maxBet,
                    stake.value,
                ));
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            console.error(error);

            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_generalFailure',
            });
        }
    }

    const { balance } = storeToRefs(useAccountStore());
    const isValidStake = computed(() => {
        if (isStakeWithinMaxBet(stakeOrVoucherStake.value, maxBet.value)
            && isStakeAtLeastMinBet(stakeOrVoucherStake.value, minBet.value)) {
            if (isRedeemVoucher.value) {
                return selectedVoucher.value?.isAvailable;
            }
            return isStakeLessBalance(
                stakeOrVoucherStake.value,
                balance.value,
                price.value,
            );
        }
        return false;
    });

    const { refreshBetCount } = useBetCountStore();

    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());

    async function placeBet() {
        if (!isValidStake.value) {
            adjustStake();
            return;
        }
        if (isPlacingBet.value) {
            return;
        }

        setIsPlacingBet(true);

        const request: DynamicMarketOrderRequest = {
            betPage: betSlip.value.betPage,
            matchId: betSlip.value.match.matchId,
            selectionId: betSlip.value.selection.id,
            stake: stakeOrVoucherStake.value,
            uid: betSlip.value.uid,
            voucherId: Number.parseInt(selectedVoucher.value?.id ?? ''),
        };
        try {
            const placeBetApi = isRedeemVoucher.value ? Api.placeDynamicMarketVoucherBet : Api.placeBetDynamicMarket;
            const [{ isPlaceBetSuccess, orderStatus, placeOrderStatus }] = await Promise.all([placeBetApi(request), sleep(placeBetDelayTime.value)]);

            if (isPlaceBetSuccess) {
                setTopScreenToast({
                    status: 'placeBetSuccess',
                    messageKey: orderStatus === StatementDetailStatus.Running ? 'betSlip_placeBetConfirmed' : 'betSlip_placeBetBeingProcessed',
                });

                setTimeout(() => {
                    refreshBetCount(false);
                    platform.updateBalance();
                    window.eventEmitter.emit('refreshBetList');
                }, 1000);

                if (!isRedeemVoucher.value) {
                    saveLastStake(stake.value);
                }
                await removeTicket();

                setShowAcceptAnyOddsBanner(true);
            } else if (placeOrderStatus === PlaceOrderStatus.RetryOpenTicket) {
                await openTicket(false);
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_generalFailure',
                });
            } else if (isPlaceHierarchyClosed(placeOrderStatus)) {
                window.location.href = getSportsBsiUrl();
            } else {
                setTopScreenToast({
                    status: 'error',
                    messageKey: getPlaceBetErrorMessage(placeOrderStatus),
                });
                refreshBetCount(false);
            }
        } catch (error) {
            console.error(error);
            setTopScreenToast({
                status: 'error',
                messageKey: getPlaceBetErrorMessage(PlaceOrderStatus.GeneralFailure),
            });
        } finally {
            setFailedPlaceBetShowAaoBanner(true);
            setIsPlacingBet(false);
        }
    }

    async function removeTicket() {
        if (hasTicket.value) {
            fireAndForget(Api.clearTicket());
        }
        setBetSlip(getDefaultDynamicMarketBetSlip());
        setBetSlipState(BetSlipState.Closed);
        setBetSlipType(BetSlipType.None);
        setIsRedeemVoucher(false);
    }

    async function clearTicketForFlowTransition() {
        const hasCurrentTicket = hasTicket.value;
        cancelToken?.cancel('Dynamic Market Ticket cleared for flow transition');
        cancelToken = undefined;
        setBetSlip(getDefaultDynamicMarketBetSlip());
        if (!hasCurrentTicket) {
            return;
        }
        try {
            await Api.clearTicket();
        } catch (error) {
            console.error(error);
        }
    }

    function adjustStake() {
        const {
            maxBet,
            minBet,
        } = betSlip.value;
        const stakeInfo = calculateStake(maxBet, minBet, balance.value, stake.value, price.value);
        setStake(stakeInfo.stake);
        setTopScreenToast({
            status: 'error',
            messageKey: stakeInfo.message,
        });
    }

    return {
        betSlip,
        stake,
        minBet,
        maxBet,
        price,
        selectedVoucher,
        isLive,
        maxPayout,
        isPlacingBet,
        isValidStake,
        betSlipMessage,
        isRedeemVoucher,
        voucherList,
        hasTicket,
        setIsRedeemVoucher,
        setStake,
        addSelection,
        removeTicket,
        clearTicketForFlowTransition,
        placeBet,
        openTicket,
        setBetSlip,
        setBetSlipMessage,
    };
});

function getDefaultDynamicMarketBetSlip(): IDynamicMarketBetSlip {
    return {
        uid: '',
        minBet: 0,
        maxBet: 0,
        match: {
            leagueId: 0,
            leagueSize: LeagueSize.Small,
            matchId: 0,
            isLive: false,
            homeTeamName: [],
            awayTeamName: [],
            sportType: 0,
            leagueName: [],
            liveHomeScore: 0,
            liveAwayScore: 0,
        },
        selection: {
            id: 0,
            name: '',
            price: null,
            score: null,
            betCondition: null,
        },
        market: {
            id: 0,
            name: '',
        },
        betPage: BetPageType.Unknown,
    };
}
